import { ReactElement, useState } from 'react';
import { ListButton, EditButton, DeleteButton, RefreshButton, useShowContext, useResourceContext } from 'react-admin';
import CardActions from '@mui/material/CardActions';
import { ActionButton } from '@/components/buttons';
import { getUrl } from '@/components/buttons/LinkButton/utils';
import { useCopyToClipboard } from '@/hooks';
import { actionButtonStyled } from '@/styles/overrides';
import { ProfileStatusEnum, TurnTargetsEnum } from '@/types/turn-enums';
import { defaults, getResumeChecksDisabledTooltip, RESUME_CHECKS_DISABLED_STATUSES } from './constants';
import { cardActionsStyled, PrimaryActions } from './styled';
import { ActionsOptions, ActionsProps } from './types';
import useController from './useController';
import { getConsentLink, getPiiTransferLink } from './utils';
import DisableConsentLinkDialog from './DisableConsentLinkDialog';
import MoveForwardDialog from './MoveForwardDialog';
import {
  MOVE_FORWARD_DISABLED_TOOLTIP,
  MOVE_FORWARD_ENABLED_TOOLTIP,
  MOVE_FORWARD_STATUSES,
} from './MoveForwardDialog/constants';
import CandidateOverview from './CandidateOverview';
import DropdpownMenu from './DropdownMenu';

/**
 * Show Actions Factory
 *
 * Creates a React-Admin <Show /> `actions` component based on feature flags.
 *
 * - Accepts a configuration object that enables/disables individual actions
 *   (edit, list, delete, status transitions, adverse actions, etc.).
 * - Returns a component compatible with the `actions` prop of <Show />.
 * - Internally consumes React-Admin context (record, resource, loading state).
 * - Action visibility can depend on both config flags and record state
 *   (e.g. profile_status).
 *
 * Usage:
 *   <Show actions={ActionFactory(options)} />
 *
 * Docs:
 *   https://marmelab.com/react-admin/Show.html#actions
 */
const ActionFactory =
  (options: ActionsOptions) =>
  ({ setEditStatus, toggleEditDispute, toggleEditNote }: Partial<ActionsProps>) => {
    const { record, isLoading } = useShowContext();
    const resource = useResourceContext();
    const config = { ...defaults, ...options };

    const turnId = record?.turn_id;
    const status = (record?.profile_status ?? '').toLowerCase();

    const { doAction, openDashboard } = useController(turnId);
    const [disableConsentConfirmOpen, setDisableConsentConfirmOpen] = useState(false);
    const [moveForwardConfirmOpen, setMoveForwardConfirmOpen] = useState(false);

    const { copyToClipboard } = useCopyToClipboard();

    if (isLoading || !record) return null;

    const canMoveForward = MOVE_FORWARD_STATUSES.includes(status);
    const canResumeChecks = !RESUME_CHECKS_DISABLED_STATUSES.includes(status);

    const actions: Array<ReactElement | null> = [
      config.edit ? <EditButton key="edit" record={record} sx={actionButtonStyled} /> : null,

      config.editStatus ? (
        <ActionButton
          iconType="edit"
          key={TurnTargetsEnum.EditStatus}
          label="Edit Status"
          onClick={() => doAction(TurnTargetsEnum.EditStatus, setEditStatus)}
        />
      ) : null,

      config.editNote &&
      (status === ProfileStatusEnum.PendingFirstNotice || status === ProfileStatusEnum.PendingSecondNotice) ? (
        <ActionButton
          iconType="edit"
          key={TurnTargetsEnum.EditNote}
          label="Edit Note"
          onClick={() => doAction(TurnTargetsEnum.EditNote, toggleEditNote)}
        />
      ) : null,

      config.editDispute ? (
        <ActionButton
          iconType="edit"
          key={TurnTargetsEnum.EditDispute}
          label="Edit Dispute"
          onClick={() => doAction(TurnTargetsEnum.EditDispute, toggleEditDispute)}
        />
      ) : null,

      config.list ? <ListButton key="list" sx={actionButtonStyled} /> : null,
      config.delete ? <DeleteButton key="delete" record={record} resource={resource} sx={actionButtonStyled} /> : null,
      config.refresh ? <RefreshButton key="refresh" sx={actionButtonStyled} /> : null,

      config.setPending && status.startsWith(ProfileStatusEnum.Review) ? (
        <ActionButton
          iconType="moreHoriz"
          key={TurnTargetsEnum.SetPending}
          label="Set pending"
          onClick={() => doAction(TurnTargetsEnum.SetPending)}
        />
      ) : null,

      config.sendPreAdverse && status === ProfileStatusEnum.PendingFirstNotice ? (
        <ActionButton
          iconType="playArrow"
          key={TurnTargetsEnum.SendPreadverse}
          label="Send Preadverse"
          onClick={() => doAction(TurnTargetsEnum.SendPreadverse)}
        />
      ) : null,

      config.sendAdverse && status === ProfileStatusEnum.PendingSecondNotice ? (
        <ActionButton
          iconType="playArrow"
          key={TurnTargetsEnum.SendAdverse}
          label="Send Adverse"
          onClick={() => doAction(TurnTargetsEnum.SendAdverse)}
        />
      ) : null,

      <DropdpownMenu
        key="viewMenu"
        items={[
          {
            label: 'Dashboard',
            iconType: 'chromeReaderMode',
            onClick: async () => await openDashboard(getUrl('dashboard', record.uuid)),
            shouldRender: Boolean(config.viewDashboard && record.uuid),
          },
          {
            label: 'Report',
            linkProps: {
              href: getUrl('report', record.report_url),
            },
            iconType: 'assignment',
            shouldRender: Boolean(config.viewReport && record.report_url),
          },
          {
            disabled: true,
            label: 'PDF (Soon)',
            iconType: 'insertDriveFileOutlined',
          },
        ]}
        label="View"
      />,

      <DropdpownMenu
        key="moreActionsMenu"
        items={[
          {
            label: 'Copy Consent Link',
            iconType: 'fileCopy',
            onClick: () =>
              copyToClipboard({
                notifyProps: {
                  onSuccess: {
                    message: 'Consent Link copied successfully',
                    options: { autoHideDuration: 100000 },
                  },
                },
                textToCopy: getConsentLink(record.report_url ?? ''),
              }),
            shouldRender: Boolean(record.report_url),
          },
          {
            label: 'Rerun Addresses',
            iconType: 'importContacts',
            onClick: () => void doAction(TurnTargetsEnum.RerunAddresses),
            shouldRender: Boolean(config.rerunAddresses && status === ProfileStatusEnum.ReviewIdentity),
          },
          {
            label: 'Copy PII transfer link',
            iconType: 'fileCopy',
            onClick: () =>
              copyToClipboard({
                notifyProps: {
                  onSuccess: {
                    message: 'PII Transfer Link copied successfully',
                    options: { autoHideDuration: 100000 },
                  },
                },
                textToCopy: getPiiTransferLink(record.report_url ?? ''),
              }),
            shouldRender: true,
          },
          {
            label: 'Disable consent link',
            iconType: 'linkOff',
            onClick: () => setDisableConsentConfirmOpen(true),
            shouldRender: Boolean(config.disableConsentLink),
          },
        ]}
        label="More Actions"
      />,
    ];

    const primaryActions: Array<ReactElement | null> = [
      config.resumeChecks ? (
        <ActionButton
          disabled={!canResumeChecks}
          iconType="playArrow"
          key={TurnTargetsEnum.ResumeChecks}
          label="Resume checks"
          onClick={() => doAction(TurnTargetsEnum.ResumeChecks)}
          tooltip={canResumeChecks ? undefined : getResumeChecksDisabledTooltip(status)}
        />
      ) : null,

      config.moveWorkerForward ? (
        <ActionButton
          disabled={!canMoveForward}
          iconType="arrowForward"
          key={TurnTargetsEnum.MoveWorkerForward}
          label="Move forward"
          onClick={() => setMoveForwardConfirmOpen(true)}
          tooltip={canMoveForward ? MOVE_FORWARD_ENABLED_TOOLTIP : MOVE_FORWARD_DISABLED_TOOLTIP}
        />
      ) : null,
    ].filter(Boolean);

    return (
      <>
        <CandidateOverview record={record} />

        <CardActions disableSpacing sx={cardActionsStyled}>
          {actions.filter(Boolean)}
          {primaryActions.length ? <PrimaryActions>{primaryActions}</PrimaryActions> : null}
        </CardActions>

        {config.disableConsentLink ? (
          <DisableConsentLinkDialog
            open={disableConsentConfirmOpen}
            onClose={() => setDisableConsentConfirmOpen(false)}
            onContinue={() => doAction(TurnTargetsEnum.DisableConsentLink)}
          />
        ) : null}

        {config.moveWorkerForward ? (
          <MoveForwardDialog
            open={moveForwardConfirmOpen}
            status={status}
            onClose={() => setMoveForwardConfirmOpen(false)}
            onContinue={() => doAction(TurnTargetsEnum.MoveWorkerForward)}
          />
        ) : null}
      </>
    );
  };

export default ActionFactory;
